import { useState } from 'react';
import { planLabel } from './labels';
import type { LearningPlan, PlanAudit, PlanChange, PlanOffer } from '../../types/learning-plans';

const time = (s: string) => new Date(s).toLocaleString('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh', dateStyle: 'medium', timeStyle: 'short' });

/**
 * An entry's target is a request, a family change, a lesson or an offer
 * version. Everything needed to name it is already on the page, so the log
 * reads "student · plan" instead of an id.
 */
function subject(targetId: string, requests: LearningPlan[], changes: PlanChange[], offers: PlanOffer[]): string {
  const forRequest = (r?: LearningPlan) => (r ? `${r.studentName} · ${r.offer.nameAr || r.offer.nameEn}` : undefined);
  const offer = offers.find((o) => o.id === targetId);
  return (
    forRequest(requests.find((r) => r.id === targetId)) ??
    forRequest(requests.find((r) => r.id === changes.find((c) => c.id === targetId)?.requestId)) ??
    forRequest(requests.find((r) => r.lessons.some((l) => l.id === targetId))) ??
    (offer ? `${offer.nameAr || offer.nameEn} · الإصدار ${offer.version}` : undefined) ??
    '—'
  );
}

export function PlanAuditTable({ audit, requests, changes, offers }: {
  audit: PlanAudit[];
  requests: LearningPlan[];
  changes: PlanChange[];
  offers: PlanOffer[];
}) {
  const [search, setSearch] = useState('');
  const rows = audit.map((a) => ({ ...a, about: subject(a.targetId, requests, changes, offers) }));
  const term = search.trim().toLocaleLowerCase('ar');
  const shown = rows.filter((a) => !term || [a.about, a.actorName ?? '', planLabel(a.action)]
    .some((value) => value.toLocaleLowerCase('ar').includes(term)));

  return (
    <div className="space-y-3">
      <section className="panel p-3" aria-label="البحث في سجل العمليات">
        <input className="input" type="search" value={search} aria-label="البحث في سجل العمليات"
          placeholder="ابحث باسم الطالب أو الخطة أو المنفّذ أو الإجراء" onChange={(e) => setSearch(e.target.value)} />
        <p className="mt-2 text-xs text-ink-3">آخر {audit.length} عملية على الخطط التعليمية.</p>
      </section>
      {shown.length === 0 ? (
        <p className="panel p-6 text-center text-ink-2">{audit.length ? 'لا توجد عمليات مطابقة.' : 'لا توجد عمليات بعد.'}</p>
      ) : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-xs text-ink-3">
              <tr>
                <th className="px-3 py-2 text-start font-medium">الوقت</th>
                <th className="px-3 py-2 text-start font-medium">الإجراء</th>
                <th className="px-3 py-2 text-start font-medium">على</th>
                <th className="hidden px-3 py-2 text-start font-medium sm:table-cell">بواسطة</th>
                <th className="hidden px-3 py-2 text-start font-medium lg:table-cell">السبب</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {shown.map((a) => (
                <tr key={a.id} data-audit-id={a.id}>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-ink-2">{time(a.createdAt)}</td>
                  <td className="px-3 py-2 font-medium">{planLabel(a.action)}
                    {a.action === 'payment.recorded' && a.details && <details className="mt-1 text-xs font-normal">
                      <summary className="cursor-pointer text-accent">تفاصيل الدفعة</summary>
                      <dl className="mt-2 space-y-1">
                        <div><dt>المبلغ</dt><dd>{typeof a.details.amountCents === 'number' ? (a.details.amountCents / 100).toFixed(2) : '—'} {typeof a.details.currency === 'string' ? a.details.currency : ''}</dd></div>
                        <div><dt>الطريقة</dt><dd>{a.details.method === 'cash' ? 'نقدًا' : 'تحويل بنكي'}</dd></div>
                        <div><dt>المرجع</dt><dd>{typeof a.details.reference === 'string' ? a.details.reference : '—'}</dd></div>
                        <div><dt>تاريخ الاستلام</dt><dd>{typeof a.details.receivedAt === 'string' ? time(a.details.receivedAt) : '—'}</dd></div>
                      </dl>
                    </details>}
                  </td>
                  <td className="px-3 py-2">{a.about}</td>
                  <td className="hidden px-3 py-2 text-ink-2 sm:table-cell">{a.actorName ?? '—'}</td>
                  <td className="hidden max-w-xs truncate px-3 py-2 text-ink-2 lg:table-cell" title={typeof a.details?.reason === 'string' ? a.details.reason : undefined}>
                    {typeof a.details?.reason === 'string' ? a.details.reason : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
